// Silo chauffeur — hub /chauffeur-prive|chauffeur-service|chauffeurservice
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { JsonLd, RouteLinks, SeoPage, TrustChips, useHreflang, useSeo } from "@/components/seo/Seo";
import { AIRPORTS, BASE_URL, BOOKING_PATH, CANTONS, CITIES, LANGS, S, T, p, routesFrom } from "@/lib/seoContent";

export const CHAUFFEUR_COPY = {
  fr: {
    kicker: "Chauffeur privé en Suisse",
    title: "Chauffeur privé en Suisse | Prix fixe, réservation en ligne | MILES",
    meta: "Chauffeur privé à Genève, Lausanne, Zurich et vers les Alpes. Prix fixe connu à l'avance, confirmation immédiate, suivi en temps réel. Réservez en 30 secondes.",
    h1: "Le chauffeur privé, en mieux.",
    intro: "MILES réinvente le transport privé : vous réservez en ligne, le prix est fixé avant la course, votre chauffeur est suivi en temps réel. Transferts aéroport, mise à disposition, longue distance — dans toute la Suisse et vers les Alpes.",
    cities: "Chauffeur privé par ville",
    airports: "Transferts aéroport",
  },
  en: {
    kicker: "Private chauffeur in Switzerland",
    title: "Chauffeur Service Switzerland | Fixed Price, Online Booking | MILES",
    meta: "Private chauffeur in Geneva, Lausanne, Zurich and to the Alps. Fixed price upfront, instant confirmation, live tracking. Book in 30 seconds.",
    h1: "The private chauffeur, simply better.",
    intro: "MILES reinvents private transport: book online, the price is fixed before the ride, your chauffeur is tracked in real time. Airport transfers, hourly chauffeur, long distance — across Switzerland and to the Alps.",
    cities: "Chauffeur service by city",
    airports: "Airport transfers",
  },
  de: {
    kicker: "Privater Chauffeur in der Schweiz",
    title: "Chauffeurservice Schweiz | Fixpreis, online buchen | MILES",
    meta: "Privater Chauffeur in Genf, Lausanne, Zürich und in die Alpen. Fixpreis im Voraus, sofortige Bestätigung, Live-Tracking. In 30 Sekunden gebucht.",
    h1: "Der private Chauffeur — einfach besser.",
    intro: "MILES erfindet den Privattransport neu: online buchen, Fixpreis vor der Fahrt, Fahrer live verfolgt. Flughafentransfers, stundenweise Fahrer, Langstrecke — in der ganzen Schweiz und in die Alpen.",
    cities: "Chauffeurservice nach Stadt",
    airports: "Flughafentransfers",
  },
};

export default function ChauffeurHub({ lang }) {
  const c = CHAUFFEUR_COPY[lang];
  const t = T[lang];
  const path = p(lang, S.chauffeur[lang]);
  const alternates = Object.fromEntries(LANGS.map((l) => [l, p(l, S.chauffeur[l])]));
  useSeo({ lang, title: c.title, description: c.meta, path, type: "chauffeur", id: "" });
  useHreflang(alternates);

  const crumbs = [{ label: t.home, to: p(lang) }, { label: c.kicker }];
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: crumbs.map((x, i) => ({ "@type": "ListItem", position: i + 1, name: x.label, ...(x.to ? { item: `${BASE_URL}${x.to}` } : {}) })) },
    { "@context": "https://schema.org", "@type": "Service", serviceType: "Private chauffeur service",
      provider: { "@type": "Organization", name: "MILES", url: BASE_URL },
      areaServed: { "@type": "Country", name: "Switzerland" } },
  ];

  return (
    <SeoPage lang={lang} breadcrumbs={crumbs} testId="seo-chauffeur-hub">
      {jsonLd.map((j, i) => <JsonLd key={i} id={`ch-${i}`} data={j} />)}

      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles">{c.kicker}</p>
      <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-tight text-ink sm:text-5xl" data-testid="chauffeur-h1">{c.h1}</h1>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate">{c.intro}</p>
      <div className="mt-7">
        <Link to={BOOKING_PATH} data-testid="chauffeur-book-cta"
          className="inline-flex h-12 items-center gap-2 rounded-xl bg-miles px-6 text-sm font-semibold text-white transition-opacity hover:opacity-90">
          {t.book_cta} <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </Link>
      </div>
      <div className="mt-8"><TrustChips lang={lang} ski /></div>

      <section className="mt-14" data-testid="chauffeur-cantons">
        <h2 className="text-xl font-bold text-ink">{t.cantons_title}</h2>
        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          {Object.values(CANTONS).map((canton) => (
            <Link key={canton.id} to={p(lang, S.chauffeur[lang], canton.slug[lang])} data-testid={`chauffeur-canton-${canton.id}`}
              className="group rounded-2xl border border-ink/10 bg-white p-6 transition-colors hover:border-miles/40">
              <MapPin className="h-5 w-5 text-miles" strokeWidth={1.5} />
              <p className="mt-3 text-base font-bold text-ink">{canton.name[lang]}</p>
              <p className="mt-1.5 line-clamp-2 text-sm text-slate">{canton.intro[lang]}</p>
              <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-miles">
                {t.view_route} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <h2 className="text-xl font-bold text-ink">{c.cities}</h2>
        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          {Object.values(CITIES).map((city) => (
            <Link key={city.id} to={p(lang, S.chauffeur[lang], city.slug[lang])} data-testid={`chauffeur-city-${city.id}`}
              className="group rounded-2xl border border-ink/10 bg-white p-6 transition-colors hover:border-miles/40">
              <MapPin className="h-5 w-5 text-miles" strokeWidth={1.5} />
              <p className="mt-3 text-base font-bold text-ink">{t.chauffeur_in} {city.name[lang]}</p>
              <p className="mt-1.5 line-clamp-2 text-sm text-slate">{city.intro[lang]}</p>
              <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-miles">
                {t.view_route} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <h2 className="text-xl font-bold text-ink">{c.airports}</h2>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {Object.values(AIRPORTS).map((a) => (
            <Link key={a.id} to={p(lang, S.airport[lang], a.slug[lang])} data-testid={`chauffeur-airport-${a.id}`}
              className="group flex items-center justify-between rounded-2xl border border-ink/10 bg-white px-6 py-5 transition-colors hover:border-miles/40">
              <span>
                <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-slate">{a.iata}</span>
                <span className="mt-1 block text-base font-bold text-ink">{a.name[lang]}</span>
              </span>
              <ArrowRight className="h-5 w-5 text-slate transition-transform group-hover:translate-x-1 group-hover:text-miles" strokeWidth={1.5} />
            </Link>
          ))}
        </div>
      </section>

      <div className="mt-14">
        <RouteLinks title={t.routes_title} routes={Object.keys(AIRPORTS).flatMap((a) => routesFrom(a)).slice(0, 6)} lang={lang} testId="chauffeur-routes" />
      </div>
    </SeoPage>
  );
}
